/**
 * Reset Password Page (DC-893)
 *
 * Allows users to set a new password using a reset token.
 */

import React, { useState, FormEvent } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { PasswordStrengthIndicator, isPasswordValid } from '../../components/auth/PasswordStrengthIndicator';
import '../../styles/auth.css';

export function ResetPassword() {
  const { token } = useParams<{ token: string }>();
  const _navigate = useNavigate();
  const { resetPassword, isLoading } = useAuth();

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!token) {
      setError('Invalid reset link');
      return;
    }

    if (!isPasswordValid(password)) {
      setError('Password does not meet the requirements');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    const result = await resetPassword(token, password);

    if (result.success) {
      setSuccess(true);
    } else {
      setError(result.error || 'Password reset failed');
    }
  };

  // Invalid token
  if (!token) {
    return (
      <div className="auth-page">
        <div className="auth-container">
          <div className="auth-card">
            <div className="auth-logo">
              <div className="auth-logo-text">DATA COMPASS</div>
            </div>

            <div className="auth-error" style={{ marginBottom: '1.5rem' }}>
              <span className="auth-error-icon">!</span>
              Invalid or expired reset link
            </div>

            <p style={{ color: 'var(--muted)', textAlign: 'center', marginBottom: '1.5rem' }}>
              This password reset link is invalid or has expired.
              Please request a new one.
            </p>

            <Link
              to="/forgot-password"
              className="auth-button"
              style={{ textDecoration: 'none', display: 'block', textAlign: 'center' }}
            >
              Request New Link
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Success state
  if (success) {
    return (
      <div className="auth-page">
        <div className="auth-container">
          <div className="auth-card">
            <div className="auth-logo">
              <div className="auth-logo-text">DATA COMPASS</div>
            </div>

            <div className="auth-success" style={{ marginBottom: '1.5rem' }}>
              <span>✓</span>
              Password reset successful!
            </div>

            <h1 className="auth-title">Password changed</h1>

            <p style={{ color: 'var(--muted)', textAlign: 'center', marginBottom: '1.5rem' }}>
              Your password has been successfully reset.
              You can now sign in with your new password.
            </p>

            <Link
              to="/login"
              className="auth-button"
              style={{ textDecoration: 'none', display: 'block', textAlign: 'center' }}
            >
              Sign In
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page">
      <div className="auth-container">
        <div className="auth-card">
          {/* Logo */}
          <div className="auth-logo">
            <div className="auth-logo-text">DATA COMPASS</div>
          </div>

          <h1 className="auth-title">Reset your password</h1>
          <p className="auth-subtitle">Enter your new password below.</p>

          {/* Error message */}
          {error && (
            <div className="auth-error">
              <span className="auth-error-icon">!</span>
              {error}
            </div>
          )}

          <form className="auth-form" onSubmit={handleSubmit}>
            {/* New Password */}
            <div className="auth-field">
              <label htmlFor="password" className="auth-label">
                New Password
              </label>
              <div className="auth-input-wrapper">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  className="auth-input"
                  placeholder="Create a strong password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="new-password"
                  autoFocus
                  disabled={isLoading}
                />
                <button
                  type="button"
                  className="auth-password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                  tabIndex={-1}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? '👁' : '👁‍🗨'}
                </button>
              </div>
              <PasswordStrengthIndicator password={password} />
            </div>

            {/* Confirm Password */}
            <div className="auth-field">
              <label htmlFor="confirmPassword" className="auth-label">
                Confirm Password
              </label>
              <div className="auth-input-wrapper">
                <input
                  id="confirmPassword"
                  type={showPassword ? 'text' : 'password'}
                  className={`auth-input ${password !== confirmPassword && confirmPassword ? 'error' : ''}`}
                  placeholder="Confirm your password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  autoComplete="new-password"
                  disabled={isLoading}
                />
              </div>
              {password !== confirmPassword && confirmPassword && (
                <div className="auth-field-error">Passwords do not match</div>
              )}
            </div>

            {/* Submit button */}
            <button type="submit" className="auth-button" disabled={isLoading}>
              {isLoading ? (
                <>
                  <span className="auth-button-spinner" />
                  Resetting...
                </>
              ) : (
                'Reset Password'
              )}
            </button>
          </form>

          {/* Links */}
          <div className="auth-links">
            <Link to="/login" className="auth-link">
              Back to Sign In
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ResetPassword;
